import { useAuth } from "@dl/web-core-js/auth";
import { Button, cn, Dialog } from "@dl/web-core-js/ui";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Eraser, Minus, Play, Plus } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShopFloorClient, type BulkStepResult, type ProductionOrderOperationDTO } from "../../api/floor";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import { partsPerUnit } from "./steps";

/** What a step still has to make in its counting unit: all it requires, less what is done. */
export function remaining(step: ProductionOrderOperationDTO): number {
  return Math.max(0, Number(step.quantity_required_counted ?? step.quantity_required) - Number(step.quantity_done ?? 0));
}

/** What is left of a step, in its counting unit: of what the step before has passed, and of what is required. */
export function leftToDo(step: ProductionOrderOperationDTO): number {
  const factor = partsPerUnit(step);
  const required = Number(step.quantity_required_counted ?? step.quantity_required);
  const available = Math.ceil(Number(step.quantity_available ?? step.quantity_required) / factor);
  return Math.max(0, Math.min(available, required) - Number(step.quantity_done ?? 0));
}

/**
 * A station's to-do list (docs/cutting-jobs §8): each row says lot, letter,
 * part and what is left, and whether it is started (and since when); the
 * started ones first, then the same part together across lots. Tap
 * a row to work on it; tick several to start them, or finish them, at once.
 * Finishing asks each row's good quantity (what is left by default); each
 * step is its own report, so a refused one is named and the others go
 * through.
 */
export function StationQueue({
  rows,
  stationId,
  operatorBadge,
  onOpen,
  onDone,
}: {
  rows: ProductionOrderOperationDTO[];
  stationId: number;
  operatorBadge: string | null;
  onOpen: (row: ProductionOrderOperationDTO) => void;
  /** After a bulk report: what went through, what did not (and why). */
  onDone: (results: BulkStepResult[]) => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const floor = useMemo(() => new ShopFloorClient(http), [http]);
  const [ticked, setTicked] = useState(new Set<number>());
  const [finishing, setFinishing] = useState(false);
  // What is under way first; then the same part together across lots: what a machine is set up for runs as one.
  const sorted = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          Number(started(b)) - Number(started(a)) ||
          (a.part_number ?? "").localeCompare(b.part_number ?? "") ||
          a.production_order_number - b.production_order_number,
      ),
    [rows],
  );
  const chosen = sorted.filter((row) => ticked.has(row.id));
  const toStart = chosen.filter((row) => !started(row));
  const bulk = useMutation({
    mutationFn: (request: { kind: "START" | "FINISH"; rows: Array<{ operation_id: number; quantity_good?: number | null }> }) =>
      floor.bulk({ ...request, station_id: stationId, operator_badge: operatorBadge }),
    onSuccess: (results) => {
      setTicked(new Set(results.filter((result) => !result.ok).map((result) => result.operation_id)));
      setFinishing(false);
      onDone(results);
    },
    onError: (failure) => onDone([{ operation_id: 0, ok: false, message: refusalMessage(failure, t("error.generic", "Something went wrong.")) }]),
  });
  const toggle = (id: number) => {
    const next = new Set(ticked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setTicked(next);
  };

  return (
    <>
      {chosen.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-line bg-accent-soft px-4 py-2">
          <span className="text-[16px] font-semibold">{t("kiosk.bulk.selected", "{{n}} selected", { n: chosen.length })}</span>
          <Button variant="secondary" icon={<Play className="size-5" />} loading={bulk.isPending && !finishing} disabled={toStart.length === 0}
            onClick={() => bulk.mutate({ kind: "START", rows: toStart.map((row) => ({ operation_id: row.id })) })}>
            {toStart.length === chosen.length
              ? t("kiosk.bulk.start", "Start them")
              : t("kiosk.bulk.start-some", "Start {{n}} not started", { n: toStart.length })}
          </Button>
          <Button variant="primary" icon={<CheckCircle2 className="size-5" />} disabled={bulk.isPending} onClick={() => setFinishing(true)}>
            {t("kiosk.bulk.finish", "Finish them")}
          </Button>
          <button type="button" className="ml-auto text-[14px] text-ink-muted underline" onClick={() => setTicked(new Set())}>
            {t("kiosk.bulk.clear", "Clear")}
          </button>
        </div>
      )}
      <ul className="flex max-h-[55vh] flex-col overflow-auto">
        {sorted.length === 0 ? (
          <li className="px-4 py-6 text-[15px] text-ink-muted">{t("kiosk.queue.empty", "Nothing waiting at this station.")}</li>
        ) : (
          sorted.map((row) => (
            <li
              key={row.id}
              className={cn(
                "flex items-stretch border-b border-l-4 border-line border-l-transparent last:border-b-0",
                started(row) && "border-l-accent",
                ticked.has(row.id) && "bg-accent-soft",
              )}
            >
              <label className="flex w-14 shrink-0 cursor-pointer items-center justify-center">
                <input
                  type="checkbox"
                  className="size-6 accent-[var(--color-accent)]"
                  checked={ticked.has(row.id)}
                  onChange={() => toggle(row.id)}
                  aria-label={t("kiosk.bulk.tick", "Select")}
                />
              </label>
              <button type="button" onClick={() => onOpen(row)} className="flex min-h-14 min-w-0 flex-1 items-center gap-3 py-2 pr-4 text-left hover:bg-surface-sunk">
                <span className="text-[17px] font-semibold tabular-nums">{row.component_code ?? row.production_order_number}</span>
                <span className="min-w-8 text-[20px] font-bold">
                  {row.letter ?? (row.box_number != null ? t("kiosk.box", "Box {{n}}", { n: row.box_number }) : "")}
                </span>
                <span className="min-w-0 flex-1 truncate text-[14px] text-ink-muted">{row.part_number}</span>
                <span className="text-[15px] tabular-nums">
                  {formatNumber(row.quantity_done)}/{formatNumber(row.quantity_required_counted ?? row.quantity_required)}
                </span>
                {started(row) && (
                  <span className="rounded bg-accent-soft px-1.5 text-[12px] font-semibold text-accent">
                    {row.started_at
                      ? t("kiosk.started-at", "Started {{time}}", {
                          time: new Date(row.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                        })
                      : t("kiosk.started", "Started")}
                  </span>
                )}
                {row.is_stalled && <span className="rounded bg-warn-soft px-1.5 text-[12px] text-warn">{t("floor.stalled", "Stalled")}</span>}
              </button>
            </li>
          ))
        )}
      </ul>
      {finishing && (
        <FinishDialog
          rows={chosen}
          busy={bulk.isPending}
          onClose={() => setFinishing(false)}
          onSubmit={(quantities) => bulk.mutate({
            kind: "FINISH",
            rows: chosen.map((row) => ({ operation_id: row.id, quantity_good: quantities[row.id] ?? 0 })),
          })}
        />
      )}
    </>
  );
}

/** A large touch button beside a quantity. */
function Step({ label, onClick, title, wide }: { label: ReactNode; onClick: () => void; title?: string; wide?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={cn(
        "flex h-12 items-center justify-center rounded-lg border border-line text-[15px] font-semibold tabular-nums hover:bg-surface-sunk",
        wide ? "px-3" : "w-12",
      )}
    >
      {label}
    </button>
  );
}

/** Under way: started and not finished. */
function started(row: ProductionOrderOperationDTO): boolean {
  return row.status === "ACTIVE";
}

/** Finishing several steps: each with the good quantity made now, all it has left filled in. */
export function FinishDialog({
  rows,
  busy,
  onClose,
  onSubmit,
}: {
  rows: ProductionOrderOperationDTO[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (quantities: Record<number, number>) => void;
}) {
  const { t } = useTranslation();
  // Each starts at all it still has to make; never below nothing.
  const [quantities, setQuantities] = useState<Record<number, number>>(() => Object.fromEntries(rows.map((row) => [row.id, remaining(row)])));
  const set = (id: number, value: number) => setQuantities((current) => ({ ...current, [id]: Math.max(0, Math.round(value)) }));
  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title={t("kiosk.bulk.finish.title", "Finish {{n}} steps", { n: rows.length })}
      description={t("kiosk.bulk.finish.hint", "Each with what was made good now; what is left of each is filled in. A step that cannot take it is named and left open.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>{t("dialog.cancel", "Cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={() => onSubmit(quantities)}>{t("kiosk.bulk.finish", "Finish them")}</Button>
        </>
      }
    >
      <ul className="flex flex-col divide-y divide-line">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center gap-3 py-2">
            <span className="w-28 text-[17px] font-semibold tabular-nums">{row.component_code ?? row.production_order_number}</span>
            <span className="w-10 text-[20px] font-bold">{row.letter}</span>
            <span className="min-w-0 flex-1 truncate text-[14px] text-ink-muted">{row.part_number}</span>
            <div className="flex items-center gap-1.5">
              <Step label={<Minus className="size-5" />} onClick={() => set(row.id, (quantities[row.id] ?? 0) - 1)} title={t("kiosk.less", "One less")} />
              {/* The number only: the buttons beside it are the controls (no spinner). */}
              <input
                type="text"
                inputMode="numeric"
                value={String(quantities[row.id] ?? 0)}
                onChange={(event) => set(row.id, Number(event.target.value.replace(/[^0-9]/g, "")) || 0)}
                onFocus={(event) => event.target.select()}
                className="h-12 w-20 rounded-lg border border-line text-center text-[22px] font-semibold tabular-nums"
                aria-label={t("kiosk.bulk.good", "Good now")}
              />
              <Step label={<Plus className="size-5" />} onClick={() => set(row.id, (quantities[row.id] ?? 0) + 1)} title={t("kiosk.more", "One more")} />
              <Step label={<Eraser className="size-5" />} onClick={() => set(row.id, 0)} title={t("kiosk.clear-quantity", "Clear")} />
              <Step label={t("kiosk.max-button", "Max")} onClick={() => set(row.id, remaining(row))} wide
                title={t("kiosk.max.title", "All it still has to make: {{n}}", { n: remaining(row) })} />
            </div>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
